import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import LandingNav from './components/LandingNav';
import HeroSection from './components/HeroSection';
import WealthCalculator from './components/WealthCalculator';
import CorpusVisualization from './components/CorpusVisualization';
import PdfCtaBand from './components/PdfCtaBand';
import Rule126Section from './components/Rule126Section';
import SiteFooter from './components/SiteFooter';
import AdminDashboard from './pages/AdminDashboard';
import { Toaster } from './components/ui/sonner';
import EntryContactModal from './components/EntryContactModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// --- API service helpers (module-level, stable references) ---
const apiCalculate = (payload) =>
  axios.post(`${BACKEND_URL}/api/calculate`, payload);

const apiDownloadBlob = (endpoint, payload) =>
  axios.post(`${BACKEND_URL}${endpoint}`, payload, { responseType: 'blob' });

const apiUpdateLeadCalculation = (leadId, payload) =>
  axios.patch(`${BACKEND_URL}/api/leads/${leadId}/calculation`, payload);

const triggerBlobDownload = (blob, mimeType, filename) => {
  try {
    // Method 1: Try native download using blob URL
    const url = window.URL.createObjectURL(new Blob([blob], { type: mimeType }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename; // Use .download property instead of setAttribute
    link.style.display = 'none';

    document.body.appendChild(link);

    // Force click with multiple fallbacks
    if (link.click) {
      link.click();
    } else if (document.createEvent) {
      const event = document.createEvent('MouseEvents');
      event.initMouseEvent('click', true, true, window, 0, 0, 0, 0, 0, false, false, false, false, 0, null);
      link.dispatchEvent(event);
    }

    // Cleanup after delay
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 250);
  } catch (err) {
    // Fallback: Try opening in new window
    try {
      const url = window.URL.createObjectURL(new Blob([blob], { type: mimeType }));
      window.open(url, '_blank');
    } catch (err2) {
      // Both methods failed - log for debugging
      console.error('Blob download fallback failed:', err2);
    }
  }
};

// --- Custom hooks (extracted from App component) ---

function useCalculation({ corpus, annualWithdrawal, returnRate, inflationRate, targetMultiplier }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCalculation = useCallback(async () => {
    if (corpus <= 0) return;
    setLoading(true);
    setError(null);
    try {
      const response = await apiCalculate({
        corpus,
        annual_withdrawal: annualWithdrawal,
        return_rate: returnRate,
        inflation_rate: inflationRate,
        target_multiplier: targetMultiplier,
      });
      setResult(response.data);
    } catch (_err) {
      setError('Failed to calculate. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [corpus, annualWithdrawal, returnRate, inflationRate, targetMultiplier]);

  useEffect(() => {
    const timer = setTimeout(fetchCalculation, 100);
    return () => clearTimeout(timer);
  }, [fetchCalculation]);

  return { result, loading, error };
}

function useReportDownload() {
  const paramsRef = useRef({ corpus: 0, annualWithdrawal: 0, returnRate: 12, inflationRate: 6, targetMultiplier: 2 });

  const updateParams = useCallback((params) => {
    paramsRef.current = params;
  }, []);

  const handleDownloadPdf = useCallback(async () => {
    const p = paramsRef.current;
    try {
      const response = await apiDownloadBlob('/api/report/pdf', {
        corpus: p.corpus,
        annual_withdrawal: p.annualWithdrawal,
        return_rate: p.returnRate,
        inflation_rate: p.inflationRate,
        target_multiplier: p.targetMultiplier,
      });

      // Verify we got data
      if (!response.data || response.data.size === 0) {
        console.error('PDF download failed: Empty response');
        return false;
      }

      // Try primary download method
      try {
        triggerBlobDownload(response.data, 'application/pdf', 'wealth_sustainability_report.pdf');
      } catch (primaryErr) {
        console.error('Primary download method failed, trying direct method...', primaryErr);

        // Fallback: Create data URL and try again
        const reader = new FileReader();
        reader.onload = function () {
          const link = document.createElement('a');
          link.href = reader.result;
          link.download = 'wealth_sustainability_report.pdf';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        };
        reader.readAsDataURL(response.data);
      }

      return true;
    } catch (err) {
      console.error('PDF download error:', err);
      return false;
    }
  }, []);

  const handleDownloadCsv = useCallback(async () => {
    const p = paramsRef.current;
    try {
      const response = await apiDownloadBlob('/api/report/csv', {
        corpus: p.corpus,
        annual_withdrawal: p.annualWithdrawal,
        return_rate: p.returnRate,
        inflation_rate: p.inflationRate,
        target_multiplier: p.targetMultiplier,
      });

      // Verify we got data
      if (!response.data || response.data.size === 0) {
        console.error('CSV download failed: Empty response');
        return false;
      }

      triggerBlobDownload(response.data, 'text/csv', 'cashflow_schedule.csv');
      return true;
    } catch (err) {
      console.error('CSV download error:', err);
      return false;
    }
  }, []);

  return { updateParams, handleDownloadPdf, handleDownloadCsv };
}

// --- Landing Page (wrapped with entry lead-capture modal) ---

function LandingPage() {
  const [corpus, setCorpus] = useState(10000000);
  const [monthlyWithdrawal, setMonthlyWithdrawal] = useState(50000);
  const [returnRate, setReturnRate] = useState(12);
  const [inflationRate, setInflationRate] = useState(6);

  // Blocking entry modal — shown until visitor submits name+mobile (or capture fails silently)
  const [entryModalOpen, setEntryModalOpen] = useState(true);
  const [leadId, setLeadId] = useState(null);
  const leadIdRef = useRef(null);
  useEffect(() => { leadIdRef.current = leadId; }, [leadId]);

  const annualWithdrawal = monthlyWithdrawal * 12;

  const { result, loading, error } = useCalculation({
    corpus, annualWithdrawal, returnRate, inflationRate, targetMultiplier: 0,
  });

  const { updateParams, handleDownloadPdf: rawDownloadPdf, handleDownloadCsv } = useReportDownload();

  // Keep ref in sync
  useEffect(() => {
    updateParams({ corpus, annualWithdrawal, returnRate, inflationRate, targetMultiplier: 0 });
  }, [corpus, annualWithdrawal, returnRate, inflationRate, updateParams]);

  // Keep latest result in a ref for use during PDF download (without re-creating callbacks)
  const resultRef = useRef(null);
  useEffect(() => { resultRef.current = result; }, [result]);

  // PDF download wrapper: patch the lead with the user's final numbers, then trigger download.
  // Patching is best-effort — failure does not block the PDF download.
  const handleDownloadPdf = useCallback(async () => {
    const leadIdNow = leadIdRef.current;
    const resultNow = resultRef.current;
    if (leadIdNow && resultNow) {
      try {
        await apiUpdateLeadCalculation(leadIdNow, {
          corpus: resultNow.initial_corpus,
          monthly_withdrawal: resultNow.initial_annual_withdrawal / 12,
          legacy_balance: resultNow.peak_balance,
          legacy_year: resultNow.peak_year,
          depletes_in: resultNow.years_until_depletion,
        });
      } catch (err) {
        console.error('Lead calculation update failed (non-blocking):', err);
      }
    }
    return rawDownloadPdf();
  }, [rawDownloadPdf]);

  const handleEntrySuccess = useCallback((newLeadId) => {
    setLeadId(newLeadId);
    setEntryModalOpen(false);
  }, []);

  const handleEntryFailSilently = useCallback(() => {
    // Let the visitor into the site even if the capture request failed
    setEntryModalOpen(false);
  }, []);

  return (
    <>
      <EntryContactModal
        open={entryModalOpen}
        onSuccess={handleEntrySuccess}
        onFailSilently={handleEntryFailSilently}
      />
      <div className="min-h-screen bg-background">
        <LandingNav />
        <main>
          <HeroSection result={result} />
          <WealthCalculator
            corpus={corpus}
            setCorpus={setCorpus}
            monthlyWithdrawal={monthlyWithdrawal}
            setMonthlyWithdrawal={setMonthlyWithdrawal}
            returnRate={returnRate}
            setReturnRate={setReturnRate}
            inflationRate={inflationRate}
            setInflationRate={setInflationRate}
            result={result}
            loading={loading}
            error={error}
            onCalculate={handleDownloadPdf}
          />
          <CorpusVisualization
            result={result}
            loading={loading}
            onDownloadCsv={handleDownloadCsv}
          />

          {/* PDF Download Button - Immediately after results */}
          {result && (
            <div className="py-8">
              <PdfCtaBand
                onDownloadPdf={handleDownloadPdf}
                result={result}
              />
            </div>
          )}

          <Rule126Section />
          <SiteFooter />
        </main>
      </div>
    </>
  );
}

// --- Main component ---

function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/admin" element={<AdminDashboard />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
